import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Siren, Play } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function QualityDriftPanel() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const q = useQuery({ queryKey: ["quality-alerts"], queryFn: async () => (await api.get("/quality-alerts/preview")).data, refetchInterval: 120_000 });
  const d = q.data;
  const canRun = ["master_admin", "admin", "mis"].includes(user.role);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/quality-alerts/run"); toast.success(data.found.length ? `${data.found.length} pattern(s) found — review below; add to Daily Meeting manually if needed` : "No quality drift found"); qc.invalidateQueries({ queryKey: ["quality-alerts"] }); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return null;
  return (
    <div className={`pp-card p-4 ${d.alerts.length ? "border-rose-200 bg-rose-50/40" : ""}`} data-testid="quality-drift-panel">
      <div className="flex flex-wrap items-center gap-2">
        <Siren size={16} className={d.alerts.length ? "text-rose-600" : "text-emerald-600"} />
        <div className="text-sm font-semibold" data-testid="quality-drift-title">{d.alerts.length ? `${d.alerts.length} customer${d.alerts.length > 1 ? "s" : ""} with pass % falling two weeks in a row` : "No quality drift detected"}</div>
        <span className="text-xs text-slate-500">{d.last_run ? `· last check ${new Date(d.last_run.at).toLocaleString()}` : ""}</span>
        {canRun && <button className="ml-auto pp-btn-ghost h-8 px-3" onClick={run} disabled={busy} data-testid="quality-drift-run"><Play size={12} /> {busy ? "Checking…" : "Check now"}</button>}
      </div>
      <div className="mt-1 text-[11px] text-slate-500" data-testid="quality-drift-note">Information only — nothing is added to the Daily Meeting automatically. Raise a point manually or by voice if the team decides to act.</div>
      {d.alerts.length > 0 && (
        <div className="mt-2 space-y-1">
          {d.alerts.map((a) => (
            <div key={`${a.kind}-${a.customer_id || a.record_id}`} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`quality-drift-${a.kind}-${a.customer_id || a.record_id}`}>
              <span className="rounded bg-slate-800 text-white px-1.5 py-0.5 font-bold uppercase">{a.kind}</span>
              <span className="font-medium">{a.customer}</span>
              {a.kind === "claim"
                ? <span className="font-mono text-rose-700">claim ₹{Math.round(a.amount || 0).toLocaleString("en-IN")} · {a.age_days} days not accepted ({a.status})</span>
                : <><span className="font-mono text-rose-700">{(a.pass || []).join("% → ")}% pass</span>
                  <span className="font-mono text-slate-500">defect {(a.defect || []).map((x) => (x == null ? "—" : x)).join("% → ")}%</span></>}
              {a.action_id ? <Link to="/meeting" className="text-blue-700 underline" data-testid={`quality-drift-link-${a.kind}-${a.customer_id || a.record_id}`}>Action point {a.action_status?.replace("_", " ")}</Link> : <Link to="/meeting" className="text-amber-700 underline" data-testid={`quality-drift-add-${a.kind}-${a.customer_id || a.record_id}`}>add to meeting manually</Link>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
